import _ from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useApi, useWorkspace } from '~/contexts';
import { useField, useIsMounted } from '~/hooks';
import AutoComplete from './AutoComplete';
import Dropdown from './Dropdown';
import FieldControl from './FieldControl';
import InlineTooltip from './InlineTooltip';
import TaskCategoryIcon from './TaskCategoryIcon';

const TaskOption = styled.span`
  display: flex;
  align-items: center;
`;

function ProjectTaskSelect({
  value,
  project,
  onSearch,
  onFilter,
  exclude,
  disableRollups = false,
  disableMilestones = false,
  ...props
}) {
  const api = useApi();
  const isMounted = useIsMounted();
  const { workspace } = useWorkspace();
  const [defaultOptions, setDefaultOptions] = useState([]);

  const handleSearch = useCallback(
    async (q) => {
      if (onSearch) {
        return onSearch(q);
      }

      const { data } = await api.www
        .workspaces(workspace.id)
        .projects(project.id)
        .tasks()
        .options({ q, projectId: project.id, recordStatusId: 'active' });

      const groupTasksByParent = (tasks) => {
        const byParent = _.groupBy(tasks, 'parentId');
        const buildOptions = (parentId = null, depth = 0) => {
          return _.sortBy(byParent[parentId] || [], 'position').flatMap((task) => [
            { type: 'task', task, depth },
            ...buildOptions(task.id, depth + 1),
          ]);
        };
        return buildOptions();
      };

      let options = groupTasksByParent(data);
      if (exclude) _.remove(options, (o) => exclude.includes(o.task.id));
      if (onFilter) options = onFilter(options);
      return options;
    },
    [workspace.id, api, project.id, onSearch, onFilter, exclude],
  );

  useEffect(() => {
    (async () => {
      const data = await handleSearch('');
      if (!isMounted.current) return;
      setDefaultOptions(data);
    })();
  }, [handleSearch, isMounted]);

  return (
    <AutoComplete
      onSearch={handleSearch}
      value={value}
      displayText={value?.name ?? ''}
      defaultOptions={defaultOptions}
      renderValue={(value) => value.name}
      {...props}>
      {({ options }) =>
        options.length > 0 ? (
          options.map((o, index) => (
            <AutoComplete.Option
              key={o.task.id}
              value={o.task}
              index={index}
              padding={`0.25rem 0 0.25rem ${1.5 + o.depth * 1}rem`}
              disabled={
                o.task.recordStatusId === 'archived' ||
                (disableRollups && o.task.type === 'rollup') ||
                (disableMilestones && o.task.type === 'milestone')
              }>
              <TaskOption>
                <TaskCategoryIcon icon={o.task.taskCategory.icon} color={o.task.taskCategory.color} />
                {o.task.name}
              </TaskOption>
              {o.task.recordStatusId === 'archived' && <InlineTooltip delay={500} message="This task is archived." />}
            </AutoComplete.Option>
          ))
        ) : (
          <Dropdown.Text>None</Dropdown.Text>
        )
      }
    </AutoComplete>
  );
}

function FieldProjectTaskSelect(props) {
  const [field, meta] = useField(props);
  const error = meta.touched && meta.error;

  return (
    <FieldControl error={error}>
      <ProjectTaskSelect {...field} {...props} />
    </FieldControl>
  );
}

export default ProjectTaskSelect;
export { FieldProjectTaskSelect };
